import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, API_ORIGIN } from '../../../services/api';
import { FiSave, FiPlus, FiTrash2, FiArrowUp, FiArrowDown, FiBook, FiFileText, FiVideo, FiList, FiEdit, FiMessageSquare, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import TheoryBlockEditor from './blocks/TheoryBlockEditor';
import SentencesBlockEditor from './blocks/SentencesBlockEditor';
import TextBlockEditor from './blocks/TextBlockEditor';
import VideoBlockEditor from './blocks/VideoBlockEditor';
import ExercisesBlockEditor from './blocks/ExercisesBlockEditor';
import DialogBlockEditor from './blocks/DialogBlockEditor';
import './CreateLessonBlocks.css';
import '../Students/StudentsPage.css';

/** Парсит correct_answer из БД: валидный JSON или «сырая» строка (true/false/not_stated и т.д.) */
function parseCorrectAnswerFromDb(raw) {
    if (raw == null || raw === '') return '';
    if (typeof raw !== 'string') return raw;
    const s = raw.trim();
    if (!s) return '';
    try {
        return JSON.parse(s);
    } catch {
        return s;
    }
}

const BLOCK_TYPES = [
    { value: 'theory', label: 'Теория', icon: <FiBook />, description: 'Текст с форматированием' },
    { value: 'sentences', label: 'Предложения', icon: <FiList />, description: 'Отдельные предложения для прослушивания' },
    { value: 'text', label: 'Текст', icon: <FiFileText />, description: 'Текст для чтения' },
    { value: 'video', label: 'Видео', icon: <FiVideo />, description: 'Загрузка видеофайла' },
    { value: 'exercises', label: 'Упражнения', icon: <FiEdit />, description: 'Интерактивные упражнения' },
    { value: 'dialog', label: 'Диалоги', icon: <FiMessageSquare />, description: 'Диалоги с озвучкой и переводом' }
];

const CreateLessonPageBlocks = ({
    renderExtraParams = null,
    onAfterLessonCreated = null,
    onAfterLessonUpdated = null,
    afterSaveNavigateTo = '/admin/view-lessons',
    cancelNavigateTo = '/admin/view-lessons',
    pageTitleCreate = 'Создать урок',
    pageTitleEdit = 'Редактировать урок',
    pageSubtitle = 'Создайте урок из различных блоков в любом порядке',
    successCreateMessage = 'Урок успешно создан',
    successEditMessage = 'Урок успешно обновлен'
} = {}) => {
    const { lessonId } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!lessonId;
    
    const [areas, setAreas] = useState([]);
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Основные параметры урока
    const [category, setCategory] = useState('grammar');
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');
    const [lessonTitle, setLessonTitle] = useState('');
    const [lessonDescription, setLessonDescription] = useState('');
    
    // Блоки урока
    const [blocks, setBlocks] = useState([]);
    
    // Состояние создания блока
    const [isAddingBlock, setIsAddingBlock] = useState(false);
    const [selectedBlockType, setSelectedBlockType] = useState('');
    
    // Состояние сворачивания блоков
    const [collapsedBlocks, setCollapsedBlocks] = useState({});
    
    // Доступные голоса для TTS
    const [availableVoices, setAvailableVoices] = useState([]);

    useEffect(() => {
        const initializeData = async () => {
            await loadInitialData();
            await loadVoices();
        };
        initializeData();
    }, []);
    
    useEffect(() => {
        if (isEditMode && areas.length > 0 && levels.length > 0) {
            loadLessonForEdit();
        }
    }, [isEditMode, lessonId, areas.length, levels.length]);

    const loadInitialData = async () => {
        try {
            const [areasRes, levelsRes] = await Promise.all([
                adminAPI.getAreas(),
                adminAPI.getLevels()
            ]);
            setAreas(areasRes.data.areas);
            setLevels(levelsRes.data.levels);
        } catch (err) {
            setError('Ошибка загрузки данных');
        }
    };

    const loadVoices = async () => {
        try {
            const response = await fetch(`${API_ORIGIN}/api/tts/voices`);
            const data = await response.json();
            const validVoices = (data.voices || []).filter(v => v.ShortName && v.DisplayName);
            setAvailableVoices(validVoices);
        } catch (error) {
            console.error('Ошибка загрузки голосов:', error);
            setAvailableVoices([]);
        }
    };

    const loadLessonForEdit = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getLessonBlocks(lessonId);
            const lesson = response.data;
            
            setCategory(lesson.category || 'grammar');
            setSelectedLevel(String(lesson.level_id) || '');
            setLessonTitle(lesson.title || '');
            setLessonDescription(lesson.description || '');
            
            if (lesson.area_id) {
                setSelectedArea(String(lesson.area_id));
            } else if (lesson.level_id) {
                const level = levels.find(l => l.id === lesson.level_id);
                if (level) {
                    setSelectedArea(String(level.area_id));
                }
            }
            
            // Преобразуем блоки в формат для редактора
            const transformedBlocks = (lesson.blocks || []).map((block, index) => {
                const transformed = {
                    id: block.id,
                    type: block.block_type,
                    title: block.title,
                    isNew: false
                };
                
                // Заполняем данные в зависимости от типа блока
                if (block.block_type === 'theory') {
                    transformed.content = block.content || '';
                    
                } else if (block.block_type === 'sentences') {
                    transformed.description = block.description || '';
                    transformed.combinedSpeechTask = !!Number(block.combined_speech_task);
                    // Преобразуем предложения из формата БД в формат редактора
                    transformed.sentences = (block.sentences || []).map(s => {
                        let parts = [];
                        if (s.semantic_parts) {
                            if (typeof s.semantic_parts === 'string') {
                                try {
                                    parts = s.semantic_parts ? JSON.parse(s.semantic_parts) : [];
                                } catch (e) {
                                    console.warn('Ошибка парсинга semantic_parts:', s.semantic_parts);
                                    parts = [];
                                }
                            } else {
                                parts = s.semantic_parts;
                            }
                        }
                        
                        return {
                            en: s.sentence_en || '',
                            ru: s.sentence_ru || '',
                            parts,
                            partsRu: [],
                            partsValid: true,
                            voiceId: s.voice_id || 'en-US-JennyNeural',
                            audioType: s.audio_url ? 'custom' : 'auto',
                            audioUrl: s.audio_url || ''
                        };
                    });
                    
                } else if (block.block_type === 'text') {
                    const textData = block.text || {};
                    transformed.textTitle = textData.title || '';
                    transformed.textDescription = textData.description || '';
                    transformed.fullTextEn = textData.full_text_en || '';
                    transformed.fullTextRu = textData.full_text_ru || '';
                    transformed.textImageUrl = textData.image_url || '';
                    transformed.textVoiceId = textData.voice_id || 'en-US-JennyNeural';
                    transformed.textAudioType = textData.audio_url ? 'custom' : 'auto';
                    transformed.textAudioUrl = textData.audio_url || '';
                    transformed.pauseBetweenSentences = textData.pause_between_sentences || 1;
                    
                    // Трансформируем предложения текста
                    transformed.textSentences = (textData.sentences || []).map(s => {
                        let parts = [];
                        if (s.semantic_parts) {
                            if (typeof s.semantic_parts === 'string') {
                                try {
                                    parts = s.semantic_parts ? JSON.parse(s.semantic_parts) : [];
                                } catch (e) {
                                    console.warn('Ошибка парсинга semantic_parts:', s.semantic_parts);
                                    parts = [];
                                }
                            } else {
                                parts = s.semantic_parts;
                            }
                        }
                        
                        return {
                            en: s.sentence_en || '',
                            ru: s.sentence_ru || '',
                            parts,
                            partsRu: [],
                            partsValid: true,
                            voiceId: s.voice_id || 'en-US-JennyNeural',
                            audioType: s.audio_url ? 'custom' : 'auto',
                            audioUrl: s.audio_url || ''
                        };
                    });
                    
                    transformed.exercises = textData.exercises || [];
                    
                } else if (block.block_type === 'video') {
                    const videoData = block.video || {};
                    transformed.videoTitle = videoData.title || '';
                    transformed.videoUrl = videoData.video_url || '';
                    transformed.videoType = videoData.video_type || 'upload';
                    
                } else if (block.block_type === 'exercises') {
                    transformed.exercises = (block.exercises || []).map(ex => ({
                        id: ex.id,
                        title: ex.title || '',
                        type: ex.exercise_type || 'multiple_choice',
                        instructions: ex.instructions || '',
                        questions: (ex.questions || []).map(q => {
                            let questionData = {};
                            let correctAnswer = '';
                            
                            // Безопасный парсинг questionData
                            if (typeof q.question_data === 'string') {
                                try {
                                    questionData = q.question_data ? JSON.parse(q.question_data) : {};
                                } catch (e) {
                                    console.warn('Ошибка парсинга question_data:', q.question_data);
                                    questionData = {};
                                }
                            } else {
                                questionData = q.question_data || {};
                            }
                            
                            // Безопасный парсинг correctAnswer (JSON или не-JSON строка: not_stated и т.п.)
                            if (typeof q.correct_answer === 'string') {
                                correctAnswer = parseCorrectAnswerFromDb(q.correct_answer);
                            } else {
                                correctAnswer = q.correct_answer;
                            }
                            
                            return {
                                id: q.id,
                                questionText: q.question_text || '',
                                questionData,
                                correctAnswer,
                                explanation: q.explanation || ''
                            };
                        })
                    }));
                    
                } else if (block.block_type === 'dialog') {
                    // Преобразуем сообщения диалога из формата БД в формат редактора
                    transformed.messages = (block.messages || []).map(msg => ({
                        speaker: msg.speaker || '',
                        messageEn: msg.message_en || '',
                        messageRu: msg.message_ru || '',
                        voiceId: msg.voice_id || 'en-US-JennyNeural',
                        audioType: msg.audio_url ? 'custom' : 'auto',
                        audioUrl: msg.audio_url || ''
                    }));
                    
                    // Преобразуем вводную лексику из формата БД в формат редактора
                    transformed.vocabulary = (block.vocabulary || []).map(vocab => ({
                        en: vocab.text_en || '',
                        ru: vocab.text_ru || '',
                        voiceId: vocab.voice_id || 'en-US-JennyNeural',
                        audioType: vocab.audio_url ? 'custom' : 'auto',
                        audioUrl: vocab.audio_url || ''
                    }));
                }
                
                return transformed;
            });
            
            setBlocks(transformedBlocks);
            setLoading(false);
        } catch (error) {
            console.error('Ошибка загрузки урока:', error);
            setError('Ошибка загрузки урока');
            setLoading(false);
        }
    };

    const filteredLevels = selectedArea 
        ? levels.filter(level => level.area_id === parseInt(selectedArea)) 
        : [];

    const handleAddBlock = (type) => {
        const newBlock = {
            id: Date.now(), // Временный ID для нового блока
            type,
            title: `${BLOCK_TYPES.find(t => t.value === type)?.label} ${blocks.length + 1}`,
            isNew: true
        };
        
        // Инициализируем данные в зависимости от типа блока
        if (type === 'theory') {
            newBlock.content = '';
        } else if (type === 'sentences') {
            newBlock.description = '';
            newBlock.combinedSpeechTask = false;
            newBlock.sentences = [{ en: '', ru: '', parts: [], voiceId: 'en-US-JennyNeural' }];
        } else if (type === 'text') {
            newBlock.textTitle = '';
            newBlock.textDescription = '';
            newBlock.fullTextEn = '';
            newBlock.fullTextRu = '';
            newBlock.textSentences = [];
            newBlock.exercises = [];
            newBlock.textVoiceId = 'en-US-JennyNeural';
            newBlock.textImageUrl = '';
            newBlock.textAudioUrl = '';
        } else if (type === 'video') {
            newBlock.videoTitle = '';
            newBlock.videoUrl = '';
            newBlock.videoType = 'upload';
        } else if (type === 'exercises') {
            newBlock.exercises = [];
        } else if (type === 'dialog') {
            newBlock.messages = [];
            newBlock.vocabulary = [];
        }
        
        setBlocks([...blocks, newBlock]);
        setIsAddingBlock(false);
        setSelectedBlockType('');
    };

    const handleUpdateBlock = (index, updatedBlock) => {
        const updated = [...blocks];
        updated[index] = { ...updated[index], ...updatedBlock };
        setBlocks(updated);
    };

    const handleRemoveBlock = (index) => {
        if (window.confirm('Вы уверены, что хотите удалить этот блок?')) {
            setBlocks(blocks.filter((_, i) => i !== index));
        }
    };

    const handleMoveBlock = (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === blocks.length - 1) return;
        
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        const updated = [...blocks];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setBlocks(updated);
    };
    
    const toggleBlockCollapse = (index) => {
        setCollapsedBlocks(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleSaveLesson = async () => {
        try {
            setLoading(true);
            setError('');
            
            if (!selectedLevel || !lessonTitle) {
                setError('Пожалуйста, заполните все обязательные поля (уровень и название урока)');
                setLoading(false);
                return;
            }
            
            const lessonData = {
                category,
                levelId: selectedLevel,
                lessonTitle,
                lessonDescription,
                blocks: blocks.map(block => {
                    const out = { type: block.type, title: block.title, ...block };
                    if (block.type === 'exercises' && (block.exercises || []).length > 0) {
                        out.exercises = block.exercises.map(ex => {
                            const exOut = { ...ex };
                            if ((ex.questions || []).length > 0) {
                                exOut.questions = ex.questions.map(q => {
                                    const qOut = { ...q };
                                    const qData = q.questionData || {};
                                    const pairs = qData.pairs;
                                    if (Array.isArray(pairs) && pairs.length > 0) {
                                        const emptyCorrect = q.correctAnswer == null || (typeof q.correctAnswer === 'object' && !Array.isArray(q.correctAnswer) && Object.keys(q.correctAnswer || {}).length === 0);
                                        if (emptyCorrect) {
                                            qOut.correctAnswer = pairs.reduce((acc, p, i) => ({ ...acc, [i]: p.right != null ? p.right : '' }), {});
                                        }
                                    }
                                    return qOut;
                                });
                            }
                            return exOut;
                        });
                    }
                    return out;
                })
            };
            
            let response;
            if (isEditMode) {
                response = await adminAPI.updateLessonBlocks(lessonId, lessonData);
            } else {
                response = await adminAPI.createLessonBlocks(lessonData);
            }

            const apiData = response?.data ? response.data : response;

            // Если создаём урок именно для материала — дадим кастомному обработчику забрать lessonId.
            if (!isEditMode && typeof onAfterLessonCreated === 'function') {
                await onAfterLessonCreated({
                    ...apiData,
                    lessonTitle,
                    lessonDescription,
                    levelId: selectedLevel,
                    category
                });
            }

            // Если редактируем урок (например, урок-материал) — вызываем обработчик обновления.
            if (isEditMode && typeof onAfterLessonUpdated === 'function') {
                await onAfterLessonUpdated({
                    ...apiData,
                    lessonTitle,
                    lessonDescription,
                    levelId: selectedLevel,
                    category
                });
            }

            setSuccess(isEditMode ? successEditMessage : successCreateMessage);
            
            setTimeout(() => {
                navigate(afterSaveNavigateTo);
            }, 1500);
            
        } catch (err) {
            console.error('Ошибка сохранения урока:', err);
            setError(
                err.response?.data?.message ||
                err.response?.data?.error ||
                'Ошибка сохранения урока'
            );
            setLoading(false);
        }
    };

    const renderBlockEditor = (block, index) => {
        const commonProps = {
            block,
            onUpdate: (updatedBlock) => handleUpdateBlock(index, updatedBlock),
            availableVoices,
            setError,
            setSuccess
        };

        switch (block.type) {
            case 'theory':
                return <TheoryBlockEditor {...commonProps} />;
            case 'sentences':
                return <SentencesBlockEditor {...commonProps} />;
            case 'text':
                return <TextBlockEditor {...commonProps} />;
            case 'video':
                return <VideoBlockEditor {...commonProps} />;
            case 'exercises':
                return <ExercisesBlockEditor {...commonProps} />;
            case 'dialog':
                return <DialogBlockEditor {...commonProps} />;
            default:
                return null;
        }
    };

    if (loading && isEditMode) {
        return (
            <div className="container" style={{ padding: '40px' }}>
                <div className="loading">Загрузка урока...</div>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '40px', maxWidth: '1400px', margin: '0 auto' }}>
            <div className="page-header" style={{ marginBottom: '30px' }}>
                <h1>{isEditMode ? pageTitleEdit : pageTitleCreate}</h1>
                <p>{pageSubtitle}</p>
            </div>

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    {error}
                </div>
            )}
            
            {success && (
                <div className="alert alert-success" style={{ marginBottom: '20px' }}>
                    {success}
                </div>
            )}

            {/* Основные параметры урока */}
            <div className="card" style={{ marginBottom: '30px' }}>
                <h2 style={{ marginBottom: '20px' }}>Основные параметры</h2>
                
                <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                        <label className="form-label required">Категория</label>
                        <select 
                            className="form-input"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            <option value="grammar">Грамматика</option>
                            <option value="vocabulary">Лексика</option>
                        </select>
                    </div>

                    <div>
                        <label className="form-label required">Область обучения</label>
                        <select 
                            className="form-input"
                            value={selectedArea}
                            onChange={(e) => {
                                setSelectedArea(e.target.value);
                                setSelectedLevel('');
                            }}
                        >
                            <option value="">Выберите область</option>
                            {areas.filter(a => a.category === category).map(area => (
                                <option key={area.id} value={area.id}>{area.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="form-label required">Уровень</label>
                        <select 
                            className="form-input"
                            value={selectedLevel}
                            onChange={(e) => setSelectedLevel(e.target.value)}
                            disabled={!selectedArea}
                        >
                            <option value="">Выберите уровень</option>
                            {filteredLevels.map(level => (
                                <option key={level.id} value={level.id}>{level.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="form-label required">Название урока</label>
                        <input 
                            type="text"
                            className="form-input"
                            placeholder="Например: Present Simple"
                            value={lessonTitle}
                            onChange={(e) => setLessonTitle(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="form-label">Описание урока (необязательно)</label>
                        <textarea 
                            className="form-input"
                            placeholder="Краткое описание урока..."
                            value={lessonDescription}
                            onChange={(e) => setLessonDescription(e.target.value)}
                            rows={3}
                        />
                    </div>
                </div>

                {renderExtraParams}
            </div>

            {/* Блоки урока */}
            <div style={{ marginBottom: '30px' }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <h2>Блоки урока ({blocks.length})</h2>
                    <div className="page-header-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {blocks.length > 0 && (
                            <button 
                                className="btn btn-secondary"
                                onClick={() => {
                                    const allCollapsed = blocks.every((_, i) => collapsedBlocks[i]);
                                    const newState = {};
                                    blocks.forEach((_, i) => {
                                        newState[i] = !allCollapsed;
                                    });
                                    setCollapsedBlocks(newState);
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                {blocks.every((_, i) => collapsedBlocks[i]) ? (
                                    <>
                                        <FiChevronDown /> Развернуть все
                                    </>
                                ) : (
                                    <>
                                        <FiChevronUp /> Свернуть все
                                    </>
                                )}
                            </button>
                        )}
                        <button 
                            className="btn btn-primary"
                            onClick={() => setIsAddingBlock(!isAddingBlock)}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <FiPlus /> Добавить блок
                        </button>
                    </div>
                </div>

                {isAddingBlock && (
                    <div className="card" style={{ marginBottom: '20px', background: '#f8f9fa' }}>
                        <h3 style={{ marginBottom: '15px' }}>Выберите тип блока</h3>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px' 
                        }}>
                            {BLOCK_TYPES.map(blockType => (
                                <div
                                    key={blockType.value}
                                    onClick={() => handleAddBlock(blockType.value)}
                                    style={{
                                        padding: '20px',
                                        background: 'white',
                                        border: '2px solid #e0e0e0',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        textAlign: 'center'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = '#4a90e2';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div style={{ fontSize: '32px', marginBottom: '10px', color: '#4a90e2' }}>
                                        {blockType.icon}
                                    </div>
                                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                                        {blockType.label}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                        {blockType.description}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: '15px', textAlign: 'right' }}>
                            <button 
                                className="btn btn-secondary"
                                onClick={() => setIsAddingBlock(false)}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                )}

                {blocks.length === 0 && !isAddingBlock && (
                    <div className="card" style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
                        <FiBook size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
                        <p>Пока нет блоков. Нажмите "Добавить блок" чтобы начать</p>
                    </div>
                )}

                {blocks.map((block, index) => {
                    const isCollapsed = collapsedBlocks[index];
                    
                    // Подсчитываем количество элементов в блоке
                    let itemCount = 0;
                    if (block.type === 'sentences' && block.sentences) {
                        itemCount = block.sentences.length;
                    } else if (block.type === 'text' && block.textSentences) {
                        itemCount = block.textSentences.length;
                    } else if (block.type === 'exercises' && block.exercises) {
                        itemCount = block.exercises.length;
                    } else if (block.type === 'dialog' && block.messages) {
                        itemCount = block.messages.length;
                    }
                    
                    return (
                    <div key={block.id || index} className="card" style={{ marginBottom: '20px', position: 'relative' }}>
                        {/* Заголовок блока с управлением */}
                        <div className="block-title-row" style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: isCollapsed ? '0' : '20px',
                            paddingBottom: isCollapsed ? '0' : '15px',
                            borderBottom: isCollapsed ? 'none' : '2px solid #e8e8e8',
                            transition: 'all 0.2s'
                        }}>
                            <div className="block-title-info" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                <div style={{ fontSize: '24px', color: '#4a90e2' }}>
                                    {BLOCK_TYPES.find(t => t.value === block.type)?.icon}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <input 
                                        type="text"
                                        className="form-input"
                                        placeholder="Название блока"
                                        value={block.title}
                                        onChange={(e) => handleUpdateBlock(index, { title: e.target.value })}
                                        style={{ 
                                            fontSize: '16px', 
                                            fontWeight: '600',
                                            maxWidth: '400px',
                                            padding: '6px 12px'
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                        {BLOCK_TYPES.find(t => t.value === block.type)?.label}
                                        {isCollapsed && itemCount > 0 && (
                                            <span style={{ 
                                                marginLeft: '10px',
                                                padding: '2px 8px',
                                                background: '#e6f7ff',
                                                color: '#1890ff',
                                                borderRadius: '10px',
                                                fontSize: '11px',
                                                fontWeight: '600'
                                            }}>
                                                {itemCount} {
                                                    block.type === 'sentences' ? 'предл.' :
                                                    block.type === 'text' ? 'предл.' :
                                                    block.type === 'exercises' ? 'упр.' :
                                                    block.type === 'dialog' ? 'сообщ.' :
                                                    'элем.'
                                                }
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="block-actions" style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    className="btn btn-icon"
                                    onClick={() => toggleBlockCollapse(index)}
                                    title={isCollapsed ? 'Развернуть' : 'Свернуть'}
                                    style={{
                                        background: isCollapsed ? '#f0f0f0' : 'white',
                                        border: '1px solid #d9d9d9'
                                    }}
                                >
                                    {isCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                                </button>
                                <button 
                                    className="btn btn-icon"
                                    onClick={() => handleMoveBlock(index, 'up')}
                                    disabled={index === 0}
                                    title="Переместить вверх"
                                >
                                    <FiArrowUp />
                                </button>
                                <button 
                                    className="btn btn-icon"
                                    onClick={() => handleMoveBlock(index, 'down')}
                                    disabled={index === blocks.length - 1}
                                    title="Переместить вниз"
                                >
                                    <FiArrowDown />
                                </button>
                                <button 
                                    className="btn btn-danger"
                                    onClick={() => handleRemoveBlock(index)}
                                    title="Удалить блок"
                                >
                                    <FiTrash2 />
                                </button>
                            </div>
                        </div>

                        {/* Редактор блока - показываем только если не свернут */}
                        {!isCollapsed && renderBlockEditor(block, index)}
                    </div>
                    );
                })}
            </div>

            {/* Кнопки сохранения */}
            <div style={{ 
                display: 'flex', 
                gap: '15px', 
                justifyContent: 'flex-end',
                position: 'sticky',
                bottom: '20px',
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
            }}>
                <button 
                    className="btn btn-secondary"
                    onClick={() => navigate(cancelNavigateTo)}
                    disabled={loading}
                >
                    Отмена
                </button>
                <button 
                    className="btn btn-primary"
                    onClick={handleSaveLesson}
                    disabled={loading || !selectedLevel || !lessonTitle}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <FiSave /> {loading ? 'Сохранение...' : 'Сохранить урок'}
                </button>
            </div>
        </div>
    );
};

export default CreateLessonPageBlocks;

// Добавляем стили для анимации
const style = document.createElement('style');
style.textContent = `
    @keyframes slideDown {
        from {
            opacity: 0;
            max-height: 0;
            overflow: hidden;
        }
        to {
            opacity: 1;
            max-height: 5000px;
            overflow: visible;
        }
    }
    
    @keyframes slideUp {
        from {
            opacity: 1;
            max-height: 5000px;
        }
        to {
            opacity: 0;
            max-height: 0;
            overflow: hidden;
        }
    }
`;
if (!document.head.querySelector('style[data-lesson-blocks]')) {
    style.setAttribute('data-lesson-blocks', 'true');
    document.head.appendChild(style);
}